import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { Bytes, SignatureLike } from '@ethersproject/bytes';
import { Contract, ethers } from 'ethers';
import { AnimatePresence, motion } from 'framer-motion';
import DiceValScreen from './DJackGameDiceValScreen';

interface IProps {
  gameState: number;
  players: string[];
  isClaimable: boolean;
  isResetable: boolean;
  contract: Contract;
  isLoading: boolean;
  scores: number[];
  entryFee: string;
}

const DJackGameDiceRoll: React.FC<IProps> = ({
  gameState,
  players,
  isClaimable,
  isResetable,
  contract,
  isLoading,
  scores,
  entryFee
}: IProps) => {
  // contract definition
  const location = useLocation();
  const path = location.pathname.split('/')[2];

  // states
  const [isFetching, setIsFetching] = useState<boolean>(false); // blockchain
  const [rollValue, setRollValue] = useState<number>(1); // roll result
  const [diceValue, setDiceValue] = useState<number>(rollValue); // display
  const [prevScore, setPrevScore] = useState<number>(0); // previous score
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false); // button
  const { account, library } = useWeb3React<Web3Provider>();

  // functions
  const handleJoin = async () => {
    if (!players.includes(ethers.constants.AddressZero)) {
      window.alert('There is no empty space in the room.');
    }
    try {
      setButtonDisabled(true);
      const receipt = await contract.join(
        path,
        players.indexOf(ethers.constants.AddressZero),
        {
          value: entryFee
        }
      );
      await receipt.wait();
      setButtonDisabled(false);
    } catch (error: any) {
      setButtonDisabled(false);
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const handleStart = async () => {
    try {
      setButtonDisabled(true);
      const receipt = await contract.startGame(path);
      await receipt.wait();
      setButtonDisabled(false);
    } catch (error: any) {
      setButtonDisabled(false);
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const handleRollSign = async (message: Bytes) => {
    const signer = library?.getSigner();
    const flatSig = await signer?.signMessage(message);
    const sig = ethers.utils.splitSignature(flatSig as SignatureLike);
    return sig;
  };

  const handleDiceRoll = async () => {
    setIsFetching(true);
    try {
      const hash = await contract.getRollHash(path);
      const bytesDataHash = ethers.utils.arrayify(hash);
      const { v, r, s } = await handleRollSign(bytesDataHash);
      await contract.rollDice(path, v, r, s);
    } catch (error: any) {
      setRollValue(1);
      setIsFetching(false);
      window.alert(error.message);
    }
  };

  const handleClaim = async () => {
    try {
      setButtonDisabled(true);
      const receipt = await contract.claim(path, 1);
      await receipt.wait();
      setButtonDisabled(false);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const handleReset = async () => {
    try {
      setButtonDisabled(true);
      const receipt = await contract.resetGame(path);
      await receipt.wait();
      setButtonDisabled(false);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  // dice roll animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDiceValue((prevDiceValue) => (prevDiceValue % 6) + 1);
    }, 700);
    if (!isFetching && diceValue === rollValue) {
      clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [diceValue, isFetching]);

  useEffect(() => {
    if (!account || !players.includes(account)) {
      return;
    }
    if (scores[players.indexOf(account)] !== prevScore && isFetching) {
      setRollValue(
        (scores[players.indexOf(account)] - prevScore) % 7 !== 0
          ? (scores[players.indexOf(account)] - prevScore) % 7
          : 1
      );
      setIsFetching(false);
      setPrevScore(scores[players.indexOf(account)]);
    }
  }, [scores]);

  // button render
  let djackButton;
  if (gameState === 1) {
    if (account && players.includes(account)) {
      djackButton = (
        <button
          type="button"
          className="buttonOutlined buttonGolden"
          onClick={handleStart}
          disabled={
            players.filter((player) => player !== ethers.constants.AddressZero)
              .length < 2 || buttonDisabled
          }
        >
          START
        </button>
      );
    } else {
      djackButton = (
        <button
          type="button"
          className="buttonOutlined buttonGreen"
          onClick={handleJoin}
          disabled={
            !account ||
            !players.includes(ethers.constants.AddressZero) ||
            buttonDisabled
          }
        >
          JOIN
        </button>
      );
    }
  } else if (gameState === 2 && !isClaimable) {
    djackButton = (
      <button
        type="button"
        className="buttonOutlined buttonBlue"
        onClick={handleDiceRoll}
        disabled={isFetching || !account || !players.includes(account)}
      >
        ROLL
      </button>
    );
  } else if (isClaimable && !isResetable) {
    djackButton = (
      <button
        type="button"
        className="buttonOutlined buttonGreen"
        onClick={handleClaim}
        disabled={!account || buttonDisabled}
      >
        CLAIM
      </button>
    );
  } else if (isResetable) {
    djackButton = (
      <>
        <button
          type="button"
          className="buttonOutlined buttonGreen"
          onClick={handleClaim}
          disabled={!account || buttonDisabled}
        >
          CLAIM
        </button>
        <button
          type="button"
          className="buttonOutlined buttonBlue"
          onClick={handleReset}
          disabled={!account || buttonDisabled}
        >
          RESET
        </button>
      </>
    );
  }

  return (
    <AnimatePresence exitBeforeEnter>
      {isLoading ? (
        <motion.div
          key="djackGameRollButtonLoader"
          variants={{
            collapsed: { opacity: 0 },
            open: {
              opacity: 1
            }
          }}
          initial="collapsed"
          animate="open"
          exit="collapsed"
        >
          <div className="spinner">
            <div className="spinner-container">
              <svg viewBox="25 25 50 50">
                <circle
                  cx="50"
                  cy="50"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                  strokeMiterlimit="10"
                />
              </svg>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="djackGameRollButtonWrapper"
          variants={{
            collapsed: { opacity: 0 },
            open: {
              opacity: 1
            }
          }}
          initial="collapsed"
          animate="open"
          exit="collapsed"
          className="djackGameDiceValWrapper"
        >
          {!isClaimable && <DiceValScreen diceValue={diceValue} />}
          {djackButton}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DJackGameDiceRoll;
